import React, { useState, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Upload as UploadIcon, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPLICANT_FIELDS = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'address', label: 'Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip_code', label: 'ZIP Code' },
  { value: 'desired_position', label: 'Desired Position' },
  { value: 'desired_coverage_areas', label: 'Coverage Areas' },
  { value: 'employment_type_preference', label: 'Employment Type' },
  { value: 'available_start_date', label: 'Available Start Date' },
  { value: 'source', label: 'Source' },
  { value: 'skip', label: '-- Skip --' },
];

const AUTO_MAP = {
  'first name': 'first_name', 'first': 'first_name', 'fname': 'first_name',
  'last name': 'last_name', 'last': 'last_name', 'lname': 'last_name',
  'email': 'email', 'e-mail': 'email',
  'phone': 'phone', 'telephone': 'phone', 'mobile': 'phone', 'cell': 'phone',
  'address': 'address', 'street': 'address',
  'city': 'city',
  'state': 'state', 'st': 'state',
  'zip': 'zip_code', 'zip code': 'zip_code', 'zipcode': 'zip_code', 'postal': 'zip_code',
  'position': 'desired_position', 'desired position': 'desired_position', 'role': 'desired_position',
  'coverage': 'desired_coverage_areas', 'coverage area': 'desired_coverage_areas',
  'employment type': 'employment_type_preference', 'type': 'employment_type_preference',
  'start date': 'available_start_date', 'available': 'available_start_date',
  'source': 'source', 'referral': 'source',
};

export default function UploadApplicants() {
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mappings, setMappings] = useState({});
  const [showMapping, setShowMapping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dupCheckResult, setDupCheckResult] = useState(null);
  const [excludedRows, setExcludedRows] = useState(new Set());
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setUploadResult(null);
    setDupCheckResult(null);
    setExcludedRows(new Set());
    parseFile(selectedFile);
  };

  const parseFile = async (f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (data.length === 0) { toast.error('No data found'); return; }
        const cols = Object.keys(data[0]);
        setColumns(cols);
        setParsedRows(data);

        // Auto-map columns
        const autoMappings = {};
        cols.forEach(col => {
          const lower = col.toLowerCase().trim();
          if (AUTO_MAP[lower]) {
            autoMappings[col] = AUTO_MAP[lower];
          } else {
            for (const [key, val] of Object.entries(AUTO_MAP)) {
              if (lower.includes(key)) { autoMappings[col] = val; break; }
            }
          }
          if (!autoMappings[col]) autoMappings[col] = 'skip';
        });
        setMappings(autoMappings);
        setShowMapping(true);
        toast.success(`Parsed ${data.length} rows, ${cols.length} columns`);
      } catch (err) {
        toast.error('Failed to parse file');
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const buildTransformedData = () => {
    return parsedRows
      .filter(row => Object.values(row).some(v => v !== null && v !== undefined && v !== ''))
      .map(row => {
        const newRow = {};
        Object.entries(row).forEach(([key, value]) => {
          const mapping = mappings[key];
          if (mapping && mapping !== 'skip') {
            newRow[mapping] = value != null ? String(value) : '';
          }
        });
        return newRow;
      })
      .filter(row => row.first_name || row.last_name);
  };

  const handleCheckDuplicates = async () => {
    const hasFirst = Object.values(mappings).includes('first_name');
    const hasLast = Object.values(mappings).includes('last_name');
    if (!hasFirst || !hasLast) { toast.error('Map First Name and Last Name'); return; }

    setChecking(true);
    try {
      const data = buildTransformedData();
      if (data.length === 0) { toast.error('No valid rows'); setChecking(false); return; }
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ats/applicants/bulk-check-duplicates`, { data }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 60000,
      });
      setDupCheckResult({ ...res.data, transformedData: data });
      setExcludedRows(new Set());
      const totalDups = (res.data.duplicates_applicant || 0) + (res.data.duplicates_personnel || 0) + (res.data.duplicates_batch || 0);
      if (totalDups > 0) toast.warning(`Found ${totalDups} potential duplicate(s)`);
      else toast.success('No duplicates found');
    } catch (err) {
      toast.error('Duplicate check failed');
    } finally {
      setChecking(false);
    }
  };

  const handleUpload = async () => {
    const hasFirst = Object.values(mappings).includes('first_name');
    const hasLast = Object.values(mappings).includes('last_name');
    if (!hasFirst || !hasLast) { toast.error('Map First Name and Last Name'); return; }

    setUploading(true);
    try {
      let data;
      if (dupCheckResult?.transformedData) {
        data = dupCheckResult.transformedData.filter((_, idx) => !excludedRows.has(idx));
      } else {
        data = buildTransformedData();
      }
      if (data.length === 0) { toast.error('No valid rows'); setUploading(false); return; }

      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ats/applicants/bulk-upload`, { data, source: 'Bulk Upload' }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 120000,
      });
      setUploadResult(res.data);
      toast.success(`${res.data.created} applicant(s) created`);
      setFile(null); setParsedRows(null); setShowMapping(false);
      setColumns([]); setMappings({}); setDupCheckResult(null); setExcludedRows(new Set());
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="upload-applicants-page">
      <div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight">Upload Applicants</h1>
        <p className="text-muted-foreground mt-2">Bulk import applicants from Excel files</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="p-8">
          <h3 className="font-heading text-xl font-semibold mb-6">Upload Excel File</h3>
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors hover:border-primary/50 ${file ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}`}
            onClick={() => fileInputRef.current?.click()}
            data-testid="applicant-drop-zone"
          >
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileSelect} data-testid="applicant-file-input" />
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileSpreadsheet size={40} className="text-primary" />
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">{parsedRows ? `${parsedRows.length} rows` : 'Parsing...'}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadIcon size={40} className="text-muted-foreground/50" />
                <p className="text-muted-foreground">Click to browse for Excel file</p>
                <p className="text-xs text-muted-foreground/60">.xlsx, .xls</p>
              </div>
            )}
          </div>

          {showMapping && columns.length > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2"><ArrowRight size={16} /> Column Mapping</h4>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {columns.map(col => (
                  <div key={col} className="flex items-center gap-3" data-testid={`mapping-${col}`}>
                    <span className="text-sm w-40 truncate font-mono bg-muted px-2 py-1 rounded">{col}</span>
                    <ArrowRight size={14} className="text-muted-foreground" />
                    <Select value={mappings[col] || 'skip'} onValueChange={v => setMappings(prev => ({ ...prev, [col]: v }))}>
                      <SelectTrigger className="w-48 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {APPLICANT_FIELDS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-muted-foreground">
                  {Object.values(mappings).includes('first_name') && Object.values(mappings).includes('last_name')
                    ? <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={14} /> Ready</span>
                    : <span className="text-amber-600">Map First Name and Last Name</span>}
                </span>
                <div className="flex gap-2">
                  <Button onClick={handleCheckDuplicates} disabled={uploading || checking} variant="outline" className="gap-2" data-testid="check-applicant-dups-btn">
                    <AlertTriangle size={16} />
                    {checking ? 'Checking...' : 'Check Duplicates'}
                  </Button>
                  <Button onClick={handleUpload} disabled={uploading} className="gap-2" data-testid="upload-applicants-btn">
                    <UploadIcon size={16} />
                    {uploading ? 'Uploading...' : (dupCheckResult ? `Upload ${dupCheckResult.transformedData.length - excludedRows.size} Applicants` : 'Upload Applicants')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-8">
          <h3 className="font-heading text-xl font-semibold mb-6">Excel Format Guidelines</h3>
          <div className="space-y-4">
            <div className="flex gap-3">
              <CheckCircle2 className="text-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-sm mb-1">Required Columns</p>
                <p className="text-sm text-muted-foreground">First Name, Last Name</p>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="text-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-sm mb-1">Optional Columns</p>
                <p className="text-sm text-muted-foreground">Email, Phone, Address, City, State, ZIP, Desired Position, Coverage Areas, Employment Type, Start Date, Source</p>
              </div>
            </div>
          </div>

          {uploadResult && (
            <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200" data-testid="upload-success">
              <CheckCircle2 className="text-green-600 mb-2" size={20} />
              <p className="font-medium text-green-800">{uploadResult.created} applicant(s) created</p>
              {uploadResult.errors?.length > 0 && (
                <p className="text-xs text-red-600 mt-1">{uploadResult.errors.length} error(s)</p>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Duplicate Check Results */}
      {dupCheckResult && (dupCheckResult.duplicates_applicant > 0 || dupCheckResult.duplicates_personnel > 0 || dupCheckResult.duplicates_batch > 0) && (
        <Card className="p-6 border-amber-200 bg-amber-50/50" data-testid="applicant-dup-results">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={20} className="text-amber-600" />
            <h3 className="font-heading text-lg font-semibold text-amber-800">
              Duplicates: {dupCheckResult.duplicates_applicant} existing applicant(s), {dupCheckResult.duplicates_personnel} personnel, {dupCheckResult.duplicates_batch} in-batch
            </h3>
          </div>
          <p className="text-sm text-amber-700 mb-4">Uncheck rows to exclude from upload.</p>
          <div className="max-h-[400px] overflow-y-auto border rounded bg-white">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-amber-100">
                <tr>
                  <th className="p-2 w-10">
                    <input type="checkbox" checked={excludedRows.size === 0}
                      onChange={e => {
                        if (e.target.checked) setExcludedRows(new Set());
                        else setExcludedRows(new Set(dupCheckResult.results.filter(r => r.duplicate_type).map(r => r.row_index)));
                      }} />
                  </th>
                  <th className="p-2 text-left">Row</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Duplicate Type</th>
                  <th className="p-2 text-left">Match Details</th>
                </tr>
              </thead>
              <tbody>
                {dupCheckResult.results.filter(r => r.duplicate_type).map((r, i) => (
                  <tr key={i} className={`border-b ${excludedRows.has(r.row_index) ? 'bg-red-50 opacity-60' : 'bg-amber-50/30'}`}>
                    <td className="p-2">
                      <input type="checkbox" checked={!excludedRows.has(r.row_index)}
                        onChange={e => { const n = new Set(excludedRows); if (e.target.checked) n.delete(r.row_index); else n.add(r.row_index); setExcludedRows(n); }} />
                    </td>
                    <td className="p-2 text-muted-foreground">{r.row_index + 1}</td>
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2">
                      <Badge className={
                        r.duplicate_type === 'personnel' ? 'bg-purple-100 text-purple-700' :
                        r.duplicate_type === 'applicant' || r.duplicate_type === 'both' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }>
                        {r.duplicate_type === 'personnel' ? 'Already Hired' : r.duplicate_type === 'both' ? 'Applicant + Hired' : r.duplicate_type === 'applicant' ? 'Existing Applicant' : 'Batch Duplicate'}
                      </Badge>
                    </td>
                    <td className="p-2 text-xs text-muted-foreground">
                      {r.matches.map((m, mi) => (
                        <span key={mi}>{m.source === 'personnel' ? '[Personnel]' : m.source === 'applicant' ? '[Applicant]' : ''} {m.name} {m.status && `(${m.status})`} {m.discipline && `- ${m.discipline}`} {m.position && `- ${m.position}`} {m.note || ''}{mi < r.matches.length - 1 ? ', ' : ''}</span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-amber-600 mt-2">{excludedRows.size > 0 ? `${excludedRows.size} row(s) will be excluded.` : 'All rows will be uploaded.'}</p>
        </Card>
      )}
    </div>
  );
}
